<template>
  <UCard class="flex flex-col h-full hover:shadow-md transition-shadow duration-200 overflow-hidden"
         :ui="{
            root: 'px-0 ring-customGray-100 ring-1',
            header: 'p-0 sm:p-0',
            body: 'flex flex-col flex-1 justify-between p-4 sm:p-4 space-y-4 border-0 ring-0',
            footer: 'p-4 sm:p-4'
         }">

    <template #header>
      <CourseCardImg :src="course.imageurl ?? imageUrlDefault" :alt="course.fullname" :to="`/curso/${createSlug(course.fullname)}`"
                     :auto_inscricao="course.auto_inscricao" :aberto_para_visitante="course.aberto_para_visitante" />
    </template>

    <div>
      <!-- Etiquetas das áreas do curso -->
      <UButton v-if="showTags" v-for="(area, keyarea) in course.grouped_fields.identificacao.area_curso" :key="keyarea"
               :label="area" :to="`/categoria/${createSlug(area)}`" variant="soft" :size="tagAreaSize"
               class="text-success-700 bg-success-50 font-bold uppercase my-1 mr-2"/>
      <!-- Título do curso -->
      <NuxtLink :to="`/curso/${createSlug(course.fullname)}`">
        <h3 class="font-bold line-clamp-2 my-2 hover:text-primary-600 transition-colors" :class="`text-${titleSize}`">
          {{ course.fullname }}
        </h3>
      </NuxtLink>

      <!-- Seção de Autores -->
      <div v-if="showAuthors" class="flex gap-3 mt-4" :class="course.authors.length < 4? 'items-center': 'flex-wrap'">
        <UAvatarGroup :class="course.authors.length < 4? '': 'w-full'" :ui="{ base: 'rounded-none squircle' }">
          <UAvatar v-for="(author, keyAuthor) in course.authors" :key="keyAuthor"
                   :alt="author.name" :src="author.avatar" class="rounded-none squircle" loading="lazy"/>
        </UAvatarGroup>

        <!-- Container de Nomes -->
        <div class="flex flex-col flex-1 min-w-0">
          <template v-for="(author, keyAuthor) in course.authors" :key="`name-${keyAuthor}`">
            <!-- Link para o Lattes (se a propriedade existir) -->
            <NuxtLink v-if="author.lattes" :to="`http://lattes.cnpq.br/${author.lattes}`" target="_blank" external
                      class="text-sm font-medium text-gray-700 dark:text-gray-300 hover:text-primary-600 truncate transition-colors">
              {{ author.name }}
            </NuxtLink>
            <span v-else class="text-sm font-medium text-gray-700 dark:text-gray-300 truncate">{{ author.name }}</span>
          </template>
        </div>
      </div>
    </div>

    <!-- Seção de Detalhes -->
    <div v-if="showDetails" class="grid grid-cols-2 gap-4 text-sm pt-6 border-t border-gray-200 dark:border-gray-800">
      <div v-if="course.grouped_fields.identificacao.carga_horaria" class="flex items-center gap-2">
        <UIcon name="ph:clock-clockwise" class="size-6 text-success-600"/>
        {{ course.grouped_fields.identificacao.carga_horaria }} h
      </div>
      <div v-if="course.grouped_fields.estatisticas.total_concluintes" class="flex items-center gap-2">
        <UIcon name="ph:users" class="size-6 text-success-600"/>
        {{ course.grouped_fields.estatisticas.total_concluintes }} alunos
      </div>
      <div v-if="course.grouped_fields.identificacao.tem_certificado" class="flex items-center gap-2">
        <UIcon name="ph:certificate" class="size-6 text-success-600"/>
        Certificado
      </div>
      <div v-if="course.grouped_fields.identificacao.eh_gratuito" class="flex items-center gap-2">
        <UIcon name="ph:currency-circle-dollar" class="size-6 text-success-600"/>
        Gratuito
      </div>
    </div>

    <template #footer>
      <div class="flex items-center justify-between w-full">
        <UButton label="Ver Detalhes" :to="`/curso/${createSlug(course.fullname)}`"
                 variant="outline" color="primary" block
                 class="font-bold text-lg ring-customGray-100 py-2"/>
      </div>
    </template>

  </UCard>
</template>

<script setup lang="ts">
import type {Course} from "~/stores/useCatalogStore";
import {createSlug} from "~/utils/strings";

// Estado reativo para controlar se a imagem já baixou
const isLoaded = ref(false)
const config = useRuntimeConfig()
const assetsPath = config.public.assetsPath

const imageUrlDefault = ref(assetsPath + '/img_categories/capa-fic-mais-ifmg.jpg')

const {
  course,
  showAuthors = false,
  showDetails = false,
  showTags = true,
  titleSize = 'xl',
  tagAreaSize = 'sm',
} = defineProps<{
  course: Course;
  showAuthors?: boolean;
  showDetails?: boolean;
  showTags?: boolean;
  titleSize?: "xs" | "sm" | "md" | "lg" | "xl" | undefined;
  tagAreaSize?: "xs" | "sm" | "md" | "lg" | "xl" | undefined;
}>()

const showBadgeInscricao = ref<boolean>(course.auto_inscricao != null)
const labelBadgeInscricao = ref<string>()
if (course.auto_inscricao == 'open') {
  labelBadgeInscricao.value = "Autoinscrição"
} else if (course.auto_inscricao == 'by_password') {
  labelBadgeInscricao.value = "Inscrição via Chave"
}

// Se a imagem falhar ao carregar (ex: link quebrado), você pode tratar aqui
const handleImageError = () => {
  isLoaded.value = true
}
</script>

<style>
.squircle {
  mask-image: url("data:image/svg+xml,%3csvg width='200' height='200' xmlns='http://www.w3.org/2000/svg'%3e%3cpath d='M100 0C20 0 0 20 0 100s20 100 100 100 100-20 100-100S180 0 100 0Z'/%3e%3c/svg%3e");
  mask-size: contain;
  mask-position: center;
  mask-repeat: no-repeat;
}
</style>